import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Home, Building2, Users, CalendarClock, ClipboardList, FileSpreadsheet,
  FileText, LogOut, Menu, Import, Upload, ScanSearch, Sparkles, Settings2,
  IdCard, Palmtree, ChevronDown, Grid2X2, Cloud, FolderArchive, CircleHelp, ShieldCheck, Inbox,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import Footer from "@/components/Footer";
import NotificationBell from "@/components/NotificationBell";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import SuiteLauncher from "@/components/SuiteLauncher";
import {
  ContextIndicator,
  ContextSwitcher,
  ImpersonationBanner,
  ImpersonationTool,
  useEffectiveRole,
} from "@/foundation/context";
import { SidebarLinks, ROLE_LABELS } from "@/foundation/shell";
import { SuiteLogo } from "@/components/suite";
import { SHELL_COMPANY_EVENT } from "@/lib/shellCompany";
import { getSuiteApplicationUrls } from "@/lib/suiteApplications";
import { useActiveCompany } from "@/contexts/ActiveCompanyContext";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const adminItems = [
  { to: "/", label: "Cruscotto", icon: Home, section: "core" },
  // v1.1.1 · Riordino Operatività: Gestione Presenze è il punto di ingresso
  // quotidiano e va per prima nella sezione (con evidenza grafica `primary`).
  { to: "/presenze", label: "Gestione Presenze", icon: ClipboardList, section: "operativita", primary: true },
  { to: "/guidata", label: "Assenze e Permessi", icon: Sparkles, section: "operativita" },
  { to: "/richieste-ferie", label: "Richieste ferie", icon: Inbox, section: "operativita" },
  { to: "/import-cartellino", label: "Import Cartellino", icon: Import, section: "operativita" },
  { to: "/calendario-ferie", label: "Calendario Assenze", icon: Palmtree, section: "pianificazione" },
  // Prechiusura release - voce menu esclusa (non approvata)
  { to: "/report", label: "Report e Stampe", icon: FileText, section: "pianificazione" },
  { to: "/riconciliazione", label: "Riconciliazione", icon: ScanSearch, section: "pianificazione" },
  { to: "/aziende", label: "Aziende", icon: Building2, section: "admin" },
  { to: "/dipendenti", label: "Dipendenti", icon: Users, section: "admin" },
  { to: "/profili-orari", label: "Profili orario", icon: CalendarClock, section: "admin" },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings2, section: "admin" },
  { to: "/import-tabulato", label: "Import Tabulato", icon: Upload, section: "advanced" },
  { to: "/import-excel", label: "Import Excel generico", icon: FileSpreadsheet, section: "advanced" },
];

const clientItems = [
  { to: "/", label: "Cruscotto", icon: Home, section: "core" },
  { to: "/presenze", label: "Gestione Presenze", icon: ClipboardList, section: "operativita", primary: true },
  { to: "/guidata", label: "Assenze e Permessi", icon: Sparkles, section: "operativita" },
  { to: "/richieste-ferie", label: "Richieste ferie", icon: Inbox, section: "operativita" },
  { to: "/import-cartellino", label: "Import Cartellino", icon: Import, section: "operativita" },
  { to: "/calendario-ferie", label: "Calendario Assenze", icon: Palmtree, section: "pianificazione" },
  // Prechiusura release - voce menu esclusa (non approvata)
  { to: "/report", label: "Report e Stampe", icon: FileText, section: "pianificazione" },
  { to: "/aziende", label: "Azienda", icon: Building2, section: "admin" },
  { to: "/dipendenti", label: "Dipendenti", icon: Users, section: "admin" },
  // v1.5.13 · Profili Orario disponibili per Amministratore Azienda (tenant-scoped
  // via user_company_roles nel backend).
  { to: "/profili-orari", label: "Profili orario", icon: CalendarClock, section: "admin" },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings2, section: "admin" },
];

const studioOperatorItems = [
  { to: "/", label: "Cruscotto", icon: Home, section: "core" },
  { to: "/presenze", label: "Gestione Presenze", icon: ClipboardList, section: "operativita", primary: true },
  { to: "/guidata", label: "Assenze e Permessi", icon: Sparkles, section: "operativita" },
  { to: "/richieste-ferie", label: "Richieste ferie", icon: Inbox, section: "operativita" },
  { to: "/import-cartellino", label: "Import Cartellino", icon: Import, section: "operativita" },
  { to: "/calendario-ferie", label: "Calendario Assenze", icon: Palmtree, section: "pianificazione" },
  // Prechiusura release - voce menu esclusa (non approvata)
  { to: "/report", label: "Report e Stampe", icon: FileText, section: "pianificazione" },
  { to: "/aziende", label: "Aziende", icon: Building2, section: "admin" },
  { to: "/dipendenti", label: "Dipendenti", icon: Users, section: "admin" },
  // v1.5.13 · Profili Orario disponibili per Collaboratore studio (tenant-scoped
  // via user_company_roles nel backend, filtro sulle aziende autorizzate).
  { to: "/profili-orari", label: "Profili orario", icon: CalendarClock, section: "admin" },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings2, section: "admin" },
];

const employeeItems = [
  { to: "/", label: "Il mio Cruscotto", icon: Home, section: "core" },
  { to: "/presenze", label: "Le mie presenze", icon: ClipboardList, section: "operativita", primary: true },
  { to: "/assenze-permessi", label: "Le mie richieste", icon: Inbox, section: "operativita" },
  // Prechiusura release - voce menu esclusa (non approvata)
  { to: "/report", label: "Il mio report", icon: FileText, section: "pianificazione" },
  { to: "/mio-profilo", label: "Il mio profilo", icon: IdCard, section: "admin" },
];

const externalConsultantItems = [
  { to: "/", label: "Cruscotto", icon: Home, section: "core" },
  { to: "/presenze", label: "Gestione Presenze", icon: ClipboardList, section: "operativita", primary: true },
  { to: "/richieste-ferie", label: "Richieste ferie", icon: Inbox, section: "operativita" },
  { to: "/calendario-ferie", label: "Calendario Assenze", icon: Palmtree, section: "pianificazione" },
  // Prechiusura release - voce menu esclusa (non approvata)
  { to: "/report", label: "Report e Stampe", icon: FileText, section: "pianificazione" },
  { to: "/aziende", label: "Aziende", icon: Building2, section: "admin" },
  { to: "/dipendenti", label: "Dipendenti", icon: Users, section: "admin" },
];

const navByRole = {
  admin: adminItems,
  super_admin: adminItems,
  root: adminItems,  // T13 . Root vede il menu completo (supervisione)
  client: clientItems,
  company_admin: clientItems,
  company_responsible: clientItems,
  studio_operator: studioOperatorItems,
  responsabile: [
    { to: "/", label: "Cruscotto", icon: Home, section: "core" },
    { to: "/impostazioni", label: "Impostazioni", icon: Settings2, section: "admin" },
  ],
  employee: employeeItems,
  external_consultant: externalConsultantItems,
};

// v1.3.0 · P2.0 STEP D1 — SECTION_LABELS, ROLE_LABELS, SidebarLinks assimilati
// in `@/foundation/shell`. Le voci di menu (navByRole) restano qui perche'
// specifiche del Product Pack GI Presenze.

function LegacyApplicationsButton({ onNavigate }) {
  const applications = [
    {
      key: "studio",
      label: "GI Studio",
      description: "Pratiche e attività dello studio",
      icon: Building2,
    },
    {
      key: "presenze",
      label: "GI Presenze",
      description: "Gestione presenze, assenze e banca ore",
      icon: ClipboardList,
      current: true,
    },
    {
      key: "pagamenti",
      label: "GI Finance",
      description: "Incassi, pagamenti, scadenze e prima nota",
      icon: FileSpreadsheet,
    },
    {
      key: "connect",
      label: "GI Connect",
      description: "Portale online per cedolini, CU e comunicazioni",
      icon: Cloud,
      unavailable: true,
    },
    {
      key: "documenti",
      label: "GI Documenti",
      description: "La memoria digitale dello Studio",
      icon: FolderArchive,
      unavailable: true,
    },
  ];
  const urls = getSuiteApplicationUrls();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="mx-3 mt-3 flex h-9 items-center gap-2 rounded-lg border border-suite-neutral-200 bg-white px-3 text-xs font-medium text-suite-neutral-700 shadow-sm hover:border-suite-primary/30 hover:text-suite-primary data-[state=open]:border-suite-primary/40 data-[state=open]:text-suite-primary"
          data-testid="suite-applications-button"
          aria-label="Apri il menu delle applicazioni Gruppo Impresa Suite"
          title="Applicazioni Gruppo Impresa Suite"
        >
          <Grid2X2 size={14} />
          <span>Applicazioni</span>
          <span className="ml-auto text-[9px] font-semibold uppercase tracking-wider text-suite-neutral-400">Suite</span>
          <ChevronDown size={12} aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="w-[330px] rounded-xl border-suite-neutral-200 p-1.5 shadow-lg"
        data-testid="suite-applications-menu"
      >
        {applications.map((application) => {
          const Icon = application.icon;
          const content = (
            <>
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                application.current
                  ? "bg-suite-primary/15 text-suite-primary"
                  : application.unavailable
                    ? "bg-suite-neutral-100 text-suite-neutral-400"
                    : "bg-suite-neutral-100 text-suite-neutral-600"
              }`}>
                <Icon size={16} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className={`block truncate text-xs font-semibold ${
                    application.unavailable ? "text-suite-neutral-500" : "text-suite-neutral-800"
                  }`}>
                    {application.label}
                  </span>
                  {application.current && (
                    <span className="rounded-full bg-suite-primary/10 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-suite-primary">
                      In uso
                    </span>
                  )}
                  {application.unavailable && (
                    <span className="rounded-full bg-suite-neutral-100 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-suite-neutral-500">
                      In preparazione
                    </span>
                  )}
                </span>
                <span className={`mt-0.5 block text-[10px] leading-4 ${
                  application.unavailable ? "text-suite-neutral-400" : "text-suite-neutral-500"
                }`}>
                  {application.description}
                </span>
              </span>
            </>
          );

          if (application.unavailable) {
            return (
              <DropdownMenuItem
                key={application.key}
                disabled
                className="flex cursor-default items-center gap-3 rounded-lg px-3 py-2.5 opacity-80"
                data-testid={`suite-app-${application.key}`}
                aria-label={`${application.label}: modulo in fase di realizzazione`}
              >
                {content}
              </DropdownMenuItem>
            );
          }

          return (
            <DropdownMenuItem key={application.key} asChild className="p-0">
              <a
                href={urls[application.key]}
                target={application.current ? undefined : "_blank"}
                rel={application.current ? undefined : "noopener noreferrer"}
                onClick={onNavigate}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 outline-none"
                data-testid={`suite-app-${application.key}`}
                aria-current={application.current ? "page" : undefined}
              >
                {content}
              </a>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const {
    companies: globalCompanies,
    activeCompany,
    activeCompanyId,
    setActiveCompanyId,
    isGlobalCompanyRole,
    loading: companyLoading,
  } = useActiveCompany();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  // Prospettiva UI: menu e label ruolo seguono il Context Engine (ruolo effettivo).
  // L'autenticazione (user.role reale) rimane inalterata.
  const effectiveRole = useEffectiveRole();
  const fallbackCompany = activeCompany?.name || user?.company_name || (["admin", "super_admin"].includes(effectiveRole) ? "Seleziona azienda" : "Gruppo Impresa Suite");
  const [companyLabel, setCompanyLabel] = useState(fallbackCompany);
  const items = useMemo(() => navByRole[effectiveRole] || [], [effectiveRole]);
  const roleLabel = ROLE_LABELS[effectiveRole] || "Utente";

  useEffect(() => setCompanyLabel(fallbackCompany), [fallbackCompany]);
  useEffect(() => {
    const onCompanySelection = (event) => setCompanyLabel(event?.detail?.name || fallbackCompany);
    window.addEventListener(SHELL_COMPANY_EVENT, onCompanySelection);
    return () => window.removeEventListener(SHELL_COMPANY_EVENT, onCompanySelection);
  }, [fallbackCompany]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };
  const sidebar = (
    <>
      <SuiteLauncher onNavigate={() => setMobileOpen(false)} />
      <SidebarLinks items={items} onNavigate={() => setMobileOpen(false)} />
    </>
  );

  return (
    <div className="flex h-screen min-h-0 flex-col overflow-hidden bg-suite-bg text-suite-neutral-800" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <ImpersonationBanner />
      <header className="z-30 flex h-14 shrink-0 items-center border-b border-suite-neutral-200 bg-white px-3 shadow-[0_1px_0_rgba(15,23,42,0.02)] md:px-5" data-testid="suite-top-header">
        <div className="flex min-w-0 items-center gap-2.5 md:w-[220px] md:shrink-0">
          <SuiteLogo size={38} />
          <div className="min-w-0 leading-tight">
            <div className="truncate text-[11px] font-bold uppercase tracking-[0.04em] text-suite-primary">Gruppo Impresa Suite</div>
            <div className="truncate text-[10px] uppercase tracking-[0.03em] text-suite-neutral-500">Gestionale Presenze</div>
          </div>
        </div>
        <div className="hidden min-w-0 flex-1 border-l border-suite-neutral-200 pl-4 md:block" data-testid="shell-company-context">
          {isGlobalCompanyRole ? (
            <div className="flex items-center gap-3">
              <div className="shrink-0">
                <div className="text-[9px] font-semibold uppercase tracking-[0.14em] text-suite-neutral-400">Azienda di lavoro</div>
                <div className="text-[10px] text-suite-neutral-500">Resta attiva in tutte le pagine</div>
              </div>
              <Select value={activeCompanyId || ""} onValueChange={setActiveCompanyId} disabled={companyLoading || !globalCompanies.length}>
                <SelectTrigger className="h-9 w-[min(360px,36vw)] border-suite-primary/30 bg-suite-primary/5 text-xs font-semibold" data-testid="global-company-selector">
                  <SelectValue placeholder={companyLoading ? "Caricamento aziende…" : "Seleziona azienda"} />
                </SelectTrigger>
                <SelectContent>
                  {globalCompanies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <>
              <div className="truncate text-xs font-bold uppercase tracking-[0.02em] text-suite-neutral-800" title={companyLabel}>{companyLabel}</div>
              <div className="text-[9px] uppercase tracking-[0.14em] text-suite-neutral-400">Azienda assegnata</div>
            </>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <ContextSwitcher trigger={<button type="button" className="focus:outline-none" data-testid="suite-context-indicator-btn"><ContextIndicator compact /></button>} />
          <ImpersonationTool />
          <Link
            to="/guida"
            state={{ from: location.pathname }}
            title="Apri il Centro Guida"
            aria-label="Apri il Centro Guida"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-suite-neutral-200 bg-white text-suite-neutral-500 transition-colors hover:border-suite-primary/30 hover:bg-suite-primary/5 hover:text-suite-primary"
            data-testid="help-manual-link"
          >
            <CircleHelp size={17} />
          </Link>
          <NotificationBell />
          <div className="hidden text-right sm:block">
            <div className="max-w-[180px] truncate text-xs font-semibold text-suite-neutral-800" title={user?.name || user?.email}>{user?.name || user?.email}</div>
            <div className="text-[9px] font-semibold uppercase tracking-[0.13em] text-suite-neutral-400">{roleLabel}</div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="flex h-8 items-center gap-1.5 rounded-md border border-suite-neutral-200 bg-white px-2.5 text-xs font-medium text-suite-neutral-600 hover:border-suite-error/30 hover:bg-suite-error/5 hover:text-suite-error"
            data-testid="logout-btn"
          >
            <LogOut size={14} />
            <span className="hidden sm:inline">Esci</span>
          </button>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-btn"><Menu size={20} /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="flex w-72 flex-col p-0">
              <div className="border-b border-suite-neutral-200 px-4 py-3">
                <div className="text-xs font-bold text-suite-primary">Gestionale Presenze</div>
                <div className="mt-1 truncate text-[11px] text-suite-neutral-500">{companyLabel}</div>
              </div>
              {sidebar}
            </SheetContent>
          </Sheet>
        </div>
      </header>
      <div className="flex min-h-0 flex-1">
        <aside className="hidden w-[240px] shrink-0 flex-col border-r border-suite-neutral-200 bg-white md:flex" data-testid="main-sidebar">
          {sidebar}
        </aside>
        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-auto p-4 md:p-6 lg:p-8" data-testid="suite-content-scroll">
            <Outlet />
          </div>
          <Footer />
        </main>
      </div>
      <ChangePasswordDialog open={changePwdOpen} onOpenChange={setChangePwdOpen} />
    </div>
  );
}
